"use client";

import Link from "next/link";
import { useRouter, usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { ChevronDown, Menu, X, Globe, Check } from "lucide-react";
import { useTranslation } from "@/lib/i18n/context";
import { localePathFor, BILINGUAL_PAGES } from "@/lib/seo/routes";
import type { Language } from "@/lib/i18n";

/** Localize a nav href to the current locale IF the page is bilingual; otherwise
 *  keep it (EN-only pages like guides/blog/legal stay on their EN path). */
function navHref(href: string, locale: Language): string {
  const pair = BILINGUAL_PAGES.find((p) => p.en === href || p.sl === href);
  return pair ? (locale === "sl" ? pair.sl : pair.en) : href;
}

interface LandingHeaderProps {
  session: { user: { name?: string | null } } | null;
}

type NavMenuItem = { label: string; desc: string; href: string };

/** UI languages offered by the marketing language switcher. Picking one sets the
 *  `lang` cookie + provider state via setLang; the register form reads the same
 *  `lang`, so the choice also seeds the new account's profile language. */
const LANGS: { code: Language; label: string }[] = [
  { code: "en", label: "English" },
  { code: "sl", label: "Slovenščina" },
];

const NAV_LINK_CLASS = "text-sm text-[var(--ink-soft)] transition hover:text-[var(--ink)]";

/** Globe dropdown — desktop language switcher (logged-out only). Hover/click to
 *  open; the active language is checked. */
function LanguageSwitcher() {
  const { setLang } = useTranslation();
  const router = useRouter();
  const pathname = usePathname() || "/";
  // Marketing language is determined by the URL (EN bare path ↔ /sl prefix), so
  // picking a language NAVIGATES to that locale's URL. We also setLang to keep
  // the cookie (and the in-app preference) in sync with the choice.
  const current: Language = pathname.startsWith("/sl") ? "sl" : "en";
  const choose = (code: Language) => {
    setLang(code);
    const target = localePathFor(pathname, code);
    if (target !== pathname) router.push(target);
    setOpen(false);
  };
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openNow = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const closeSoon = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };
  return (
    <div className="relative" onMouseEnter={openNow} onMouseLeave={closeSoon}>
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Language"
        onClick={() => setOpen((v) => !v)}
        onFocus={openNow}
        className="grid h-9 w-9 place-items-center rounded-lg text-[var(--ink-soft)] outline-none transition hover:bg-[var(--paper-2)] hover:text-[var(--ink)]"
      >
        <Globe className="h-[18px] w-[18px]" />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 pt-2" onMouseEnter={openNow} onMouseLeave={closeSoon}>
          <div className="w-[180px] rounded-xl border border-[var(--rule)] bg-[var(--paper)] p-1.5 shadow-[0_12px_28px_rgba(26,24,21,0.14)]">
            {LANGS.map((l) => (
              <button
                key={l.code}
                type="button"
                onClick={() => choose(l.code)}
                className="flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-[14px] text-[var(--ink)] transition hover:bg-[var(--paper-2)]"
              >
                <span>{l.label}</span>
                {current === l.code && <Check className="h-4 w-4 text-[var(--terracotta)]" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Hover-opened navbar dropdown (Product / Guides). Opens on hover or focus, and
 *  a short close delay + an invisible `pt-2` bridge keep it open while the cursor
 *  crosses the gap into the panel. Click still toggles it for touch/keyboard. */
function MenuDropdown({ label, items }: { label: string; items: NavMenuItem[] }) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openNow = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const closeSoon = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };
  return (
    <div className="relative" onMouseEnter={openNow} onMouseLeave={closeSoon}>
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onFocus={openNow}
        className={`inline-flex items-center gap-1 outline-none ${NAV_LINK_CLASS} ${open ? "text-[var(--ink)]" : ""}`}
      >
        {label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-50 pt-2" onMouseEnter={openNow} onMouseLeave={closeSoon}>
          <div className="w-[300px] rounded-xl border border-[var(--rule)] bg-[var(--paper)] p-1.5 shadow-[0_12px_28px_rgba(26,24,21,0.14)]">
            {items.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                onClick={() => setOpen(false)}
                className="flex flex-col items-start gap-0.5 rounded-lg px-3 py-2.5 transition hover:bg-[var(--paper-2)]"
              >
                <span className="text-[14px] font-medium text-[var(--ink)]">{item.label}</span>
                <span className="text-[12px] leading-snug text-[var(--ink-soft)]">{item.desc}</span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function LandingHeader({ session }: LandingHeaderProps) {
  const { t, setLang } = useTranslation();
  const nav = t.landing.cover.nav;
  const router = useRouter();
  const pathname = usePathname() || "/";
  const currentLocale: Language = pathname.startsWith("/sl") ? "sl" : "en";
  // Localize bilingual nav targets so a Slovenian visitor stays in Slovenian.
  const productMenu = nav.productMenu.map((i) => ({ ...i, href: navHref(i.href, currentLocale) }));
  const pricingHref = navHref("/pricing", currentLocale);
  const homeHref = currentLocale === "sl" ? "/sl" : "/";
  const [mobileOpen, setMobileOpen] = useState(false);

  // While the mobile menu is open, hide the page's fixed "Start free trial" bar
  // (HeroSection) — it sits at z-50 and otherwise covers the menu's bottom rows
  // (the language switcher), making them untappable.
  useEffect(() => {
    document.body.classList.toggle("nav-mobile-open", mobileOpen);
    return () => document.body.classList.remove("nav-mobile-open");
  }, [mobileOpen]);

  return (
    <div className="sticky top-0 z-50 bg-[color-mix(in_oklab,var(--paper)_72%,transparent)] backdrop-blur-md backdrop-saturate-150 border-b border-[var(--hair)]">
      <nav className="max-w-[1320px] mx-auto px-6 md:px-12 py-4 md:py-[18px] grid grid-cols-[1fr_auto] md:grid-cols-[1fr_auto_1fr] items-center gap-6">
        <Link href={homeHref} className="flex items-center gap-2.5">
          <span className="relative w-[22px] h-[22px] rounded-full border-[1.5px] border-[var(--ink)]">
            <span className="absolute inset-[4px] rounded-full bg-[var(--terracotta)]" />
          </span>
          <span className="font-display text-[22px] font-medium tracking-[-0.02em]">
            Journ<em className="italic font-medium">ai</em>l
          </span>
        </Link>

        {/* Desktop center menu */}
        <div className="hidden md:flex items-center gap-7">
          <MenuDropdown label={nav.product} items={productMenu} />
          <Link href={pricingHref} className={NAV_LINK_CLASS}>{nav.pricing}</Link>
          <MenuDropdown label={nav.guides} items={nav.guidesMenu} />
        </div>

        {/* Right cluster (desktop) */}
        <div className="hidden md:flex justify-self-end items-center gap-3 md:gap-5 text-sm">
          {session?.user ? (
            <>
              {/* Logged in: globe stays but doesn't switch inline — language is
                 managed in the profile, so it links to Settings → Language. */}
              <Link
                href="/settings#language"
                aria-label="Language"
                className="grid h-9 w-9 place-items-center rounded-lg text-[var(--ink-soft)] transition hover:bg-[var(--paper-2)] hover:text-[var(--ink)]"
              >
                <Globe className="h-[18px] w-[18px]" />
              </Link>
              <Link
                href="/today"
                className="inline-flex items-center gap-2 px-[18px] py-[10px] rounded-[10px] bg-[var(--terracotta)] text-[var(--terracotta-soft)] font-medium whitespace-nowrap transition hover:-translate-y-px hover:shadow-[0_8px_20px_rgba(184,83,26,0.28)]"
              >
                {t.landing.hero.openApp}
              </Link>
            </>
          ) : (
            <>
              <LanguageSwitcher />
              <Link href="/login" className="text-[var(--ink-soft)] hover:text-[var(--ink)]">
                {nav.signIn}
              </Link>
              <Link
                href="/register"
                className="inline-flex items-center gap-2 px-[18px] py-[10px] rounded-[10px] bg-[var(--terracotta)] text-[var(--terracotta-soft)] font-medium whitespace-nowrap transition hover:-translate-y-px hover:shadow-[0_8px_20px_rgba(184,83,26,0.28)]"
              >
                {nav.startTrial}
              </Link>
            </>
          )}
        </div>

        {/* Mobile cluster: language globe (dropdown with both languages) + hamburger */}
        <div className="md:hidden justify-self-end flex items-center gap-0.5">
          {session?.user ? (
            <Link
              href="/settings#language"
              aria-label="Language"
              className="grid h-9 w-9 place-items-center rounded-lg text-[var(--ink-soft)] hover:bg-[var(--paper-2)] hover:text-[var(--ink)]"
            >
              <Globe className="h-[18px] w-[18px]" />
            </Link>
          ) : (
            <LanguageSwitcher />
          )}
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={nav.product}
            aria-expanded={mobileOpen}
            className="grid h-9 w-9 place-items-center rounded-lg text-[var(--ink)] hover:bg-[var(--paper-2)]"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div className="md:hidden border-t border-[var(--hair)] bg-[var(--paper)] px-6 py-4 max-h-[calc(100dvh-58px)] overflow-y-auto overscroll-contain pb-[calc(20px+env(safe-area-inset-bottom))]">
          <div className="flex flex-col gap-1">
            <p className="mt-1 mb-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-soft)]/70">{nav.product}</p>
            {productMenu.map((item) => (
              <Link key={item.href} href={item.href} onClick={() => setMobileOpen(false)} className="rounded-lg px-2 py-2 text-[15px] text-[var(--ink)] hover:bg-[var(--paper-2)]">
                {item.label}
              </Link>
            ))}
            <Link href={pricingHref} onClick={() => setMobileOpen(false)} className="mt-1 rounded-lg px-2 py-2 text-[15px] text-[var(--ink)] hover:bg-[var(--paper-2)]">
              {nav.pricing}
            </Link>
            <p className="mt-3 mb-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-soft)]/70">{nav.guides}</p>
            {nav.guidesMenu.map((item) => (
              <Link key={item.href} href={item.href} onClick={() => setMobileOpen(false)} className="rounded-lg px-2 py-2 text-[15px] text-[var(--ink)] hover:bg-[var(--paper-2)]">
                {item.label}
              </Link>
            ))}
            <div className="mt-3 flex flex-col gap-2 border-t border-[var(--hair)] pt-3">
              {session?.user ? (
                <>
                  <Link
                    href="/settings#language"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 rounded-lg px-2 py-2 text-[15px] text-[var(--ink)] hover:bg-[var(--paper-2)]"
                  >
                    <Globe className="h-4 w-4 text-[var(--ink-soft)]" /> {t.settings.language}
                  </Link>
                  <Link href="/today" onClick={() => setMobileOpen(false)} className="mt-1 inline-flex items-center justify-center rounded-[10px] bg-[var(--terracotta)] px-4 py-2.5 font-medium text-[var(--terracotta-soft)]">
                    {t.landing.hero.openApp}
                  </Link>
                </>
              ) : (
                <>
                  <p className="mb-0.5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-soft)]/70">
                    <Globe className="h-3.5 w-3.5" /> Language
                  </p>
                  {LANGS.map((l) => (
                    <button
                      key={l.code}
                      type="button"
                      onClick={() => {
                        setLang(l.code);
                        const target = localePathFor(pathname, l.code);
                        if (target !== pathname) router.push(target);
                        setMobileOpen(false);
                      }}
                      className="flex items-center justify-between rounded-lg px-2 py-2 text-[15px] text-[var(--ink)] hover:bg-[var(--paper-2)]"
                    >
                      <span>{l.label}</span>
                      {currentLocale === l.code && <Check className="h-4 w-4 text-[var(--terracotta)]" />}
                    </button>
                  ))}
                  <Link href="/login" onClick={() => setMobileOpen(false)} className="mt-1 rounded-lg px-2 py-2 text-[15px] text-[var(--ink-soft)] hover:text-[var(--ink)]">
                    {nav.signIn}
                  </Link>
                  <Link href="/register" onClick={() => setMobileOpen(false)} className="inline-flex items-center justify-center rounded-[10px] bg-[var(--terracotta)] px-4 py-2.5 font-medium text-[var(--terracotta-soft)]">
                    {nav.startTrial}
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
